import { useEffect, useState, useCallback } from "react";
import { saveAs } from "file-saver";
import { useParams } from "react-router-dom";
import { useCaseOfficerServiceContext } from "../ServiceContext";
import { useApproveSelfEvaluationRequirementByCaseOfficerMutation, useGetAdditionalFieldValueBySelfEvalRequirementIdForAdminMutation, useGetFileListBySelfEvalRequirementIdForAdminMutation, useLazyGetSelfEvaluationRequirementBySelfEvaluationIdForAdminQuery, useRejectSelfEvaluationRequirementByCaseOfficerMutation, useSubmitClarificationForSelfEvaluationRequirementByCaseOfficerMutation } from "../../../app/services/selfEvalutionReviewService/selfEvalutionReviewServiceApi";
import { useDeleteSelfEvalutionUploadedFileByIdMutation, useDownloadAllTypesDocForSelfEvalutionMutation, useUploadPdfForSelfEvalutionMutation } from "../../../app/services/SelfEvaluationForm/selfEvalutionServiceApi";
import { decryptAES } from "../../../services/CryptoService";
import ToastService from "../../../services/toastService/ToastService";
import { decryptAESID } from "../../../utils/Encryption";

export const buildFieldKey = (rawFieldId, brandId) => brandId != null ? `${rawFieldId}_${brandId}` : `${rawFieldId}`;

export function useSelfEvaluationSection(section, { useListQuery, useFieldDefsQuery, onApplicationId }) {
    const { id } = useParams();
    const { state, dispatch } = useCaseOfficerServiceContext();
    const selfEvaluationId = state?.sections?.[section]?.selfEvaluationId;
    const applicationStatus = state?.sections?.[section]?.applicationStatus;
    const isLocked = applicationStatus === "Rejected" || applicationStatus === "Approval" || applicationStatus === "Approved" || applicationStatus === "Discontinued" || applicationStatus == "MUA Endorsed";

    const [sectionRecord, setSectionRecord] = useState([]);
    const [fieldList, setFieldList] = useState([]);
    const [approvingReqId, setApprovingReqId] = useState(null);
    const [rejectingReqId, setRejectingReqId] = useState(null);
    const [clarifyingReqId, setClarifyingReqId] = useState(null);

    const [getList, { isLoading: listLoading, isSuccess: listSuccess, data: listData }] = useListQuery();
    const [getFieldDefs, { isLoading: fieldDefsLoading, isSuccess: fieldDefsSuccess, data: fieldDefsData }] = useFieldDefsQuery();
    const [getFileList, { isLoading: fileListLoading, isSuccess: fileListSuccess, data: fileListData }] = useGetFileListBySelfEvalRequirementIdForAdminMutation();
    const [getFieldValues, { isLoading: fieldValuesLoading, isSuccess: fieldValuesSuccess, data: fieldValuesData }] = useGetAdditionalFieldValueBySelfEvalRequirementIdForAdminMutation();
    const [getRequirementBySelfEvalId, { isLoading: reqLoading, isSuccess: reqSuccess, data: reqData }] = useLazyGetSelfEvaluationRequirementBySelfEvaluationIdForAdminQuery();
    const [uploadPDF] = useUploadPdfForSelfEvalutionMutation();
    const [downloadDoc] = useDownloadAllTypesDocForSelfEvalutionMutation();
    const [deleteFileById] = useDeleteSelfEvalutionUploadedFileByIdMutation();
    const [approveRequirement, { isLoading: approveLoading, isSuccess: approveSuccess, data: approveData }] = useApproveSelfEvaluationRequirementByCaseOfficerMutation();
    const [rejectRequirement, { isLoading: rejectLoading, isSuccess: rejectSuccess, data: rejectData }] = useRejectSelfEvaluationRequirementByCaseOfficerMutation();
    const [sendClarification, { isLoading: clarificationLoading, isSuccess: clarificationSuccess, data: clarificationData }] = useSubmitClarificationForSelfEvaluationRequirementByCaseOfficerMutation();

    const refreshRequirements = useCallback(() => {
        if (selfEvaluationId > 0) getRequirementBySelfEvalId(selfEvaluationId);
    }, [selfEvaluationId, getRequirementBySelfEvalId]);

    // ─── FILE UPLOAD
    const handleFileUpload = useCallback(
        async (e, reqId) => {
            if (isLocked) return;
            const file = e.target.files[0];
            if (!file) return;
            try {
                const formData = new FormData();
                formData.append("File", file);
                formData.append("StoragePath", "SelfEvalutionDocs");
                const res = await uploadPDF(formData).unwrap();
                const data = await decryptAES(res?.responseData);
                dispatch({
                    type: "ADD_FILE",
                    section,
                    reqId,
                    selfEvalRequirementId: state.sections?.[section]?.requirements?.[reqId]?.selfEvalRequirementId,
                    file: { id: String(Date.now()), name: data?.fileName, url: data?.filePath },
                });
                ToastService.success("File uploaded successfully");
            } catch (err) {
                ToastService.error("File upload failed");
            }
        },
        [isLocked, uploadPDF, dispatch, section, state.sections]
    );

    // ─── FILE DOWNLOAD
    const handleDownload = useCallback(
        async (file) => {
            if (isLocked) return;
            try {
                const req = { DocumentType: file?.name?.split(".").pop(), Path: file?.url };
                const response = await downloadDoc(req);
                if (response?.data) saveAs(response.data, file?.name || "downloaded_file");
            } catch (error) {
                console.error("Download failed:", error);
            }
        },
        [isLocked, downloadDoc]
    );

    // ─── DELETE FILE (server-side)
    const handleDeleteFile = useCallback(
        async (reqId, file) => {
            try {
                const response = await deleteFileById({ Id: file.id, FileName: file?.name, FilePath: file?.url });
                if (response?.data?.hasError === false) {
                    dispatch({ type: "DELETE_FILE", section, reqId, fileId: file?.id });
                    ToastService.success(response?.data?.message || "File deleted successfully");
                } else {
                    ToastService.error("Something went wrong");
                }
            } catch (error) {
                console.error("Delete file error:", error);
            }
        },
        [deleteFileById, dispatch, section]
    );

    const handleDelete = useCallback(
        async (reqId, file) => {
            if (isLocked) return;
            if (typeof file.id !== "string") {
                await handleDeleteFile(reqId, file);
            } else {
                dispatch({ type: "DELETE_FILE", section, reqId, fileId: file?.id });
                ToastService.success("File deleted successfully");
            }
        },
        [isLocked, handleDeleteFile, dispatch, section]
    );

    // ─── REQUIREMENT COMMENT / CASE OFFICER COMMENT
    const handleComment = useCallback(
        (reqId, value) => {
            if (isLocked) return;
            dispatch({
                type: "SET_REQ_COMMENT",
                section,
                reqId,
                comment: value,
                selfEvalRequirementId: state.sections?.[section]?.requirements?.[reqId]?.selfEvalRequirementId,
                caseOfficerComments: state.sections?.[section]?.requirements?.[reqId]?.caseOfficerComments,
            });
        },
        [isLocked, dispatch, section, state.sections]
    );

    const handleCaseOfcComment = useCallback(
        (reqId, value) => {
            if (isLocked) return;
            dispatch({
                type: "SET_REQ_COMMENT",
                section,
                reqId,
                comment: state.sections?.[section]?.requirements?.[reqId]?.comment,
                selfEvalRequirementId: state.sections?.[section]?.requirements?.[reqId]?.selfEvalRequirementId,
                caseOfficerComments: value,
                status: state.sections?.[section]?.requirements?.[reqId]?.status,
                peerReviewerComments: state.sections?.[section]?.requirements?.[reqId]?.peerReviewerComments,
            });
        },
        [isLocked, dispatch, section, state.sections]
    );

    // ─── ADDITIONAL FIELD VALUE / COMMENT (brandId optional → works for A, B, C)
    const handleFieldChange = useCallback(
        (rawFieldId, value, reqId, brandId = null) => {
            if (isLocked) return;
            const key = buildFieldKey(rawFieldId, brandId);
            const selfEvalRequirementId = state.sections?.[section]?.fields?.[key]?.selfEvalRequirementId;
            const existingId = state.sections?.[section]?.fields?.[key]?.id;
            dispatch({
                type: "SET_FIELD",
                section,
                fieldId: key,
                value,
                reqId,
                selfEvalRequirementId,
                id: existingId || null,
                additionalFieldId: rawFieldId,
                brandItemId: brandId || null,
            });
        },
        [isLocked, dispatch, section, state.sections]
    );

    const handleFieldComment = useCallback(
        (rawFieldId, value, reqId, brandId = null) => {
            if (isLocked) return;
            const key = buildFieldKey(rawFieldId, brandId);
            const selfEvalRequirementId = state.sections?.[section]?.fields?.[key]?.selfEvalRequirementId;
            const existingId = state.sections?.[section]?.fields?.[key]?.id;
            dispatch({
                type: "SET_FIELD_COMMENT",
                section,
                fieldId: key,
                comment: value,
                reqId,
                selfEvalRequirementId,
                id: existingId || null,
                additionalFieldId: rawFieldId,
                brandItemId: brandId || null,
            });
        },
        [isLocked, dispatch, section, state.sections]
    );

    const handleFieldBlur = useCallback(
        (fieldId) => {
            dispatch({ type: "TOUCH_FIELD", section, fieldId });
        },
        [dispatch, section]
    );

    const isFieldRequired = useCallback(
        (reqId, isRequired) => {
            if (!isRequired) return false;
            if (section === "C") {
                const hasFile = (state.sections?.[section]?.requirements?.[reqId]?.files || []).length > 0;
                if (!hasFile) return false;
            }
            return true;
        },
        [state.sections, section]
    );

    const isFieldInvalid = useCallback(
        (fieldId, isRequired, reqId) => {
            if (!isFieldRequired(reqId, isRequired)) return false;
            const fieldState = state.sections?.[section]?.fields?.[fieldId];
            const isEmpty = !fieldState?.value && fieldState?.value !== false && fieldState?.value !== 0;
            const shouldShow = fieldState?.touched || state.submitAttempted;
            return isEmpty && shouldShow;
        },
        [state.sections, state.submitAttempted, section, isFieldRequired]
    );

    // const isFieldInvalid = useCallback(
    //     (fieldId, isRequired) => {
    //         if (!isRequired) return false;
    //         const fieldState = state.sections?.[section]?.fields?.[fieldId];
    //         const isEmpty = !fieldState?.value && fieldState?.value !== false && fieldState?.value !== 0;
    //         const shouldShow = fieldState?.touched || state.submitAttempted;
    //         return isEmpty && shouldShow;
    //     },
    //     [state.sections, state.submitAttempted, section]
    // );

    // ─── APPROVE / REJECT / CLARIFICATION
    const handleApprove = useCallback(
        (reqId) => {
            setApprovingReqId(reqId);
            approveRequirement({
                SelfEvaluationRequirementId: state.sections?.[section]?.requirements?.[reqId]?.selfEvalRequirementId,
                CaseOfficerRequirementComment: state.sections?.[section]?.requirements?.[reqId]?.caseOfficerComments,
            });
        },
        [approveRequirement, state.sections, section]
    );

    const handleReject = useCallback(
        (reqId) => {
            setRejectingReqId(reqId);
            rejectRequirement({
                SelfEvaluationRequirementId: state.sections?.[section]?.requirements?.[reqId]?.selfEvalRequirementId,
                CaseOfficerRequirementComment: state.sections?.[section]?.requirements?.[reqId]?.caseOfficerComments,
            });
        },
        [rejectRequirement, state.sections, section]
    );

    const handleClarificationSend = useCallback(
        (reqId) => {
            setClarifyingReqId(reqId);
            sendClarification({
                SelfEvaluationRequirementId: state.sections?.[section]?.requirements?.[reqId]?.selfEvalRequirementId,
                ClarificationComments: state.sections?.[section]?.requirements?.[reqId]?.caseOfficerComments,
            });
        },
        [sendClarification, state.sections, section]
    );

    // ─── EFFECTS (identical logic previously repeated 3x, now defined once)
    useEffect(() => {
        const applicationId = decryptAESID(id?.toString(), false);
        if (applicationId) {
            getList(applicationId);
            getFieldDefs(applicationId);
            onApplicationId?.(applicationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (selfEvaluationId > 0) getRequirementBySelfEvalId(selfEvaluationId);
    }, [selfEvaluationId, getRequirementBySelfEvalId]);

    useEffect(() => {
        if (!listLoading && listSuccess && listData) {
            setSectionRecord(listData);
            dispatch({ type: "SET_REQ_LIST", section, reqList: listData });
        }
    }, [listLoading, listSuccess, listData, dispatch, section]);

    useEffect(() => {
        if (!fieldDefsLoading && fieldDefsSuccess && fieldDefsData) {
            const data = fieldDefsData.map((item) => ({ ...item, isRequired: item?.ismandatory }));
            dispatch({ type: "SET_FIELD_LIST", section, fieldList: data });
            setFieldList(data);
        }
    }, [fieldDefsLoading, fieldDefsSuccess, fieldDefsData, dispatch, section]);

    useEffect(() => {
        if (!reqLoading && reqSuccess && reqData) {
            const ids = reqData.map((item) => item.selfEvalRequirementId);
            if (ids.length > 0 && selfEvaluationId > 0) {
                getFileList({ selfEvaluationId, requirementIds: ids });
                getFieldValues({ selfEvaluationId, requirementIds: ids });
            }
            reqData.forEach((item) => {
                dispatch({
                    type: "SET_REQ_COMMENT",
                    section: item?.section,
                    reqId: item?.requirementId,
                    comment: item?.comment,
                    selfEvalRequirementId: item?.selfEvalRequirementId,
                    caseOfficerComments: item?.clarificationComments,
                    status: item.statusName,
                    peerReviewerComments: item?.peerReviewerComment,
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reqLoading, reqSuccess, reqData, selfEvaluationId, getFileList, getFieldValues]);

    useEffect(() => {
        if (!fileListLoading && fileListSuccess && fileListData) {
            fileListData.forEach((item) => {
                const existingFiles = state.sections?.[section]?.requirements?.[item?.requirementId]?.files || [];
                const alreadyExists = existingFiles.some(
                    (f) => f.name === item?.selfEvaluationFileName && f.url === item?.selfEvaluationFilePath
                );
                if (!alreadyExists) {
                    dispatch({
                        type: "ADD_FILE",
                        section,
                        reqId: item?.requirementId,
                        selfEvalRequirementId: item?.selfEvalRequirementId,
                        file: {
                            id: item?.fileId,
                            name: item?.selfEvaluationFileName,
                            url: item?.selfEvaluationFilePath,
                        },
                    });
                }
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }
    }, [fileListLoading, fileListSuccess, fileListData]);

    useEffect(() => {
        if (!fieldValuesLoading && fieldValuesSuccess && fieldValuesData) {
            const filtered = fieldValuesData?.filter((field) =>
                fieldDefsData?.some((v) => v?.sgbpAdditionalFieldInputId === field?.additionalFieldId)
            );
            filtered?.forEach((item) => {
                const key = buildFieldKey(item?.additionalFieldId, item?.productDetailsId);
                dispatch({
                    type: "SET_FIELD",
                    section,
                    fieldId: key,
                    value: item.additionalFieldValue,
                    reqId: item.requirementId,
                    selfEvalRequirementId: item.selfEvalRequirementId,
                    id: item.id,
                    additionalFieldId: item.additionalFieldId,
                    brandItemId: item?.productDetailsId || null,
                });
                dispatch({
                    type: "SET_FIELD_COMMENT",
                    section,
                    fieldId: key,
                    comment: item.comments,
                    reqId: item?.requirementId,
                    selfEvalRequirementId: item.selfEvalRequirementId,
                    id: item.id,
                    additionalFieldId: item.additionalFieldId,
                    brandItemId: item?.productDetailsId || null,
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldValuesLoading, fieldValuesSuccess, fieldValuesData, fieldDefsData]);

    useEffect(() => {
        if (!approveLoading && approveSuccess && approveData) {
            ToastService.success("Requirement Approve");
            refreshRequirements();
        }
    }, [approveLoading, approveSuccess, approveData, refreshRequirements]);

    useEffect(() => {
        if (!rejectLoading && rejectSuccess && rejectData) {
            ToastService.success("Requirement Reject");
            refreshRequirements();
        }
    }, [rejectLoading, rejectSuccess, rejectData, refreshRequirements]);

    useEffect(() => {
        if (!clarificationLoading && clarificationSuccess && clarificationData) {
            ToastService.success("Clarification has been added to the list.");
            refreshRequirements();
        }
    }, [clarificationLoading, clarificationSuccess, clarificationData, refreshRequirements]);

    return {
        state,
        dispatch,
        isLocked,
        sectionRecord,
        fieldList,
        selfEvaluationId,
        handleFileUpload,
        handleDownload,
        handleDelete,
        handleComment,
        handleCaseOfcComment,
        handleFieldChange,
        handleFieldComment,
        handleFieldBlur,
        isFieldInvalid,
        handleApprove,
        handleReject,
        handleClarificationSend,
        approveLoading,
        rejectLoading,
        clarificationLoading,
        rawRequirementRows: reqData,
        isFieldRequired,  
        approvingReqId,   
        rejectingReqId,   
        clarifyingReqId,  
    };
}